/**
 * Spend — what this organization's agents cost over a period, by member, team,
 * workspace, agent or model, and the monthly budgets set against it. An owner's
 * and an administrator's page (`readSpend` answers anybody else 404).
 *
 * Every figure is the runs' estimated cost at API list prices, from the trail
 * of runs: an organization that does not record runs has nothing here, and the
 * page says how to turn it on. A budget warns the owners and administrators by
 * mail at 80 % and 100 % of a month, once each, and tells each member's app —
 * it never stops a run, and the page says that too.
 */
import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { formatTokens, formatUsd } from '@airlock/contracts/usage/analysis';
import {
  SPEND_GROUPS,
  SPEND_GROUP_WORDS,
  type BudgetScope,
  type BudgetStatus,
  type SpendAnswer,
  type SpendGroup
} from '@airlock/contracts/runs/spend';
import type { TeamList } from '@airlock/contracts/cloud/wire';
import { color, font, radius } from '../../theme/tokens';
import { Callout, Empty, Input, Row, RowAction, StatStrip, Table } from '../../components/primitives';
import { useConfirm } from '../../components/Modal';
import { HeaderButton } from '../../components/Shell';
import { api, apiPath } from '../../lib/api';
import type { Organization, WorkspaceList } from '../../lib/types';
import { failureText, selectStyle } from './shared';
import { Segmented } from '../../components/FilterBar';
import { SkeletonPage } from '../../components/Loading';
import { BudgetMeter, DailySpend } from './spend-chart';

type Period = 'month' | 'last' | '30' | '90';
const PERIODS: { id: Period; label: string }[] = [
  { id: 'month', label: 'This month' },
  { id: 'last', label: 'Last month' },
  { id: '30', label: '30 days' },
  { id: '90', label: '90 days' }
];

function periodOf(id: Period, now = new Date()): { from: string; to: string } {
  const day = (date: Date): string => date.toISOString().slice(0, 10);
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  if (id === 'month') return { from: day(new Date(Date.UTC(y, m, 1))), to: day(now) };
  if (id === 'last') return { from: day(new Date(Date.UTC(y, m - 1, 1))), to: day(new Date(Date.UTC(y, m, 0))) };
  const back = new Date(now);
  back.setUTCDate(back.getUTCDate() - (Number(id) - 1));
  return { from: day(back), to: day(now) };
}

export function SpendPage({ organization }: { organization: Organization | null }): React.JSX.Element {
  const [period, setPeriod] = useState<Period>('month');
  const [by, setBy] = useState<SpendGroup>('member');
  const [answer, setAnswer] = useState<SpendAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const span = useMemo(() => periodOf(period), [period]);

  useEffect(() => {
    if (!organization) return;
    setError(null);
    api
      .get<SpendAnswer>(apiPath`/orgs/${organization.id}/spend?from=${span.from}&to=${span.to}&by=${by}`)
      .then(setAnswer)
      .catch((failure: unknown) => setError(failureText(failure, 'The spend could not be read.')));
  }, [organization?.id, span.from, span.to, by]);

  if (!organization) return <Empty title="Spend belongs to an organization">A personal account’s costs stay on its machine.</Empty>;

  const total = answer?.total;
  const columns = 'minmax(0,1.6fr) 80px 100px 110px minmax(0,1.2fr)';
  const most = Math.max(1, ...(answer?.lines ?? []).map((line) => line.costMicros));

  return (
    <div style={{ display: 'grid', gap: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <Segmented label="Period" value={period} options={PERIODS} onChange={setPeriod} />
        <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkFaint }}>
          {span.from} → {span.to} · UTC
        </span>
      </div>

      {error && <p style={{ fontSize: 13.5, color: color.badText, margin: 0 }}>{error}</p>}

      {answer && total && total.runs === 0 ? (
        <Callout tone="teal" title="No run recorded in this period">
          Spend is added up from the trail of runs. Turn on Trail of runs in Configuration › Agents › All agents and publish, and each member’s Airlock
          sends a record of every run in this organization’s workspaces from then on.
        </Callout>
      ) : null}

      <StatStrip
        items={[
          { label: 'Estimated cost', value: total ? formatUsd(total.costMicros) : '—', spark: answer?.days.map((one) => one.costMicros) },
          { label: 'Tokens', value: total ? formatTokens(total.tokens) : '—' },
          { label: 'Runs', value: total ? String(total.runs) : '—' },
          {
            label: 'Per run',
            value: total && total.runs ? formatUsd(Math.round(total.costMicros / total.runs)) : '—',
            note: total && total.runs ? `${formatUsd(total.costMicros)} ÷ ${total.runs}` : undefined
          }
        ]}
      />

      <section style={{ background: color.surface, border: `1px solid ${color.line}`, borderRadius: radius.xxl, padding: '18px 22px 16px' }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Estimated cost per day</div>
        {answer ? <DailySpend days={answer.days} /> : <SkeletonPage blocks={1} />}
      </section>

      <Table
        columns={columns}
        header={[SPEND_GROUP_WORDS[by], 'Runs', 'Tokens', 'Cost', 'Share']}
        toolbar={
          <>
            <div style={{ fontSize: 15, fontWeight: 600 }}>By {SPEND_GROUP_WORDS[by].toLowerCase()}</div>
            <span style={{ flex: 1 }} />
            <Segmented
              label="Group by"
              value={by}
              options={SPEND_GROUPS.map((one) => ({ id: one, label: SPEND_GROUP_WORDS[one] }))}
              onChange={setBy}
            />
          </>
        }
      >
        {(answer?.lines ?? []).map((line) => (
          <Row key={line.key || '—'} columns={columns}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', color: line.key ? color.ink : color.inkSubtle }}>{line.label}</span>
            <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{line.runs}</span>
            <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{formatTokens(line.tokens)}</span>
            <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.ink }}>{formatUsd(line.costMicros)}</span>
            {/* A share bar (redesign §42.6): the share of the total written in
                mono, then a 6 px track filled relative to the largest line (6 px
                at least), filling once as it first appears. */}
            <span style={{ display: 'grid', gridTemplateColumns: '36px minmax(0,1fr)', gap: 8, alignItems: 'center' }}>
              <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkMuted, textAlign: 'right' }}>
                {total && total.costMicros ? `${Math.round((line.costMicros / total.costMicros) * 100)}%` : '0%'}
              </span>
              <span aria-hidden="true" style={{ height: 6, borderRadius: 3, background: color.line, overflow: 'hidden' }}>
                <span
                  className="al-share"
                  style={{ display: 'block', height: '100%', width: `max(6px, ${(line.costMicros / most) * 100}%)`, background: color.accent, borderRadius: 3 }}
                />
              </span>
            </span>
          </Row>
        ))}
        {/* The total row in the table's foot (§42.9): 600, under a strong
            hairline, the organization's own figures — by team they can be
            less than the lines' sum, which the note under the table says. */}
        {total && (answer?.lines ?? []).length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: columns,
              gap: 16,
              minHeight: 40,
              padding: '8px 12px',
              borderTop: `1px solid ${color.lineStrong}`,
              marginTop: -1,
              fontSize: 13.5,
              fontWeight: 600,
              alignItems: 'center'
            }}
          >
            <span>Total</span>
            <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{total.runs}</span>
            <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{formatTokens(total.tokens)}</span>
            <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.ink }}>{formatUsd(total.costMicros)}</span>
            <span />
          </div>
        )}
      </Table>
      {by === 'team' && (
        <p style={{ margin: '-10px 0 0', fontSize: 12.5, color: color.inkSubtle }}>
          A member in two teams counts in both, so the teams can add up to more than the total.
        </p>
      )}

      <Budgets organization={organization} budgets={answer?.budgets ?? null} onChange={(budgets) => setAnswer((current) => (current ? { ...current, budgets } : current))} />
    </div>
  );
}

const SCOPE_WORDS: Record<BudgetScope, string> = { org: 'Organization', team: 'Team', workspace: 'Workspace' };

/** The monthly budgets: each with its meter, and a line to set one. */
function Budgets({
  organization,
  budgets,
  onChange
}: {
  organization: Organization;
  budgets: BudgetStatus[] | null;
  onChange: (budgets: BudgetStatus[]) => void;
}): React.JSX.Element {
  const [dialog, ask] = useConfirm();
  const [scope, setScope] = useState<BudgetScope>('org');
  const [target, setTarget] = useState('');
  const [dollars, setDollars] = useState('');
  const [teams, setTeams] = useState<{ id: string; name: string }[]>([]);
  const [workspaces, setWorkspaces] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const columns = 'minmax(0,1.4fr) 120px minmax(0,1.6fr) 80px';

  useEffect(() => {
    api
      .get<TeamList>(apiPath`/orgs/${organization.id}/teams`)
      .then((list) => setTeams(list.teams))
      .catch(() => setTeams([]));
    api
      .get<WorkspaceList>(apiPath`/orgs/${organization.id}/workspaces`)
      .then((list) => setWorkspaces(list.workspaces))
      .catch(() => setWorkspaces([]));
  }, [organization.id]);

  const choices = scope === 'team' ? teams : scope === 'workspace' ? workspaces : [];
  const amount = Number(dollars);
  const ready = dollars.trim() !== '' && Number.isFinite(amount) && amount >= 0 && (scope === 'org' || Boolean(target));

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const next = await api.send<{ budgets: BudgetStatus[] }>(
        'PUT',
        apiPath`/orgs/${organization.id}/budgets`,
        { scope, scopeId: scope === 'org' ? null : target, monthlyUsd: amount },
        {}
      );
      onChange(next.budgets);
      setDollars('');
    } catch (failure) {
      setError(failureText(failure, 'That budget was not kept.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string, label: string): Promise<void> => {
    // A budget's mails go with it: asked first (proposal 22).
    const agreed = await ask({
      title: `Remove the ${label} budget?`,
      lede: 'Its monthly limit and the mails at 80 % and at 100 % go. The runs and what they cost stay — a budget never stopped any of them.',
      confirm: 'Remove',
      danger: true
    });
    if (!agreed) return;
    setError(null);
    try {
      onChange((await api.del<{ budgets: BudgetStatus[] }>(apiPath`/orgs/${organization.id}/budgets/${id}`)).budgets);
    } catch (failure) {
      setError(failureText(failure, 'That budget was not removed.'));
    }
  };

  const pick: React.CSSProperties = { ...selectStyle, width: 'auto', height: 34, fontSize: 13, padding: '0 10px', borderRadius: radius.md };

  return (
    <>
      {dialog}
      <Table
        columns={columns}
        header={['Budget', 'Per month', 'This month', '']}
        toolbar={
          <>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Monthly budgets</div>
            <span style={{ flex: 1 }} />
            <select aria-label="Budget for" value={scope} onChange={(event) => {
                setScope(event.target.value as BudgetScope);
                setTarget('');
              }} style={pick}>
              {(['org', 'team', 'workspace'] as const).map((one) => (
                <option key={one} value={one}>
                  {SCOPE_WORDS[one]}
                </option>
              ))}
            </select>
            {scope !== 'org' && (
              <select aria-label={SCOPE_WORDS[scope]} value={target} onChange={(event) => setTarget(event.target.value)} style={pick}>
                <option value="">Choose a {scope}</option>
                {choices.map((one) => (
                  <option key={one.id} value={one.id}>
                    {one.name}
                  </option>
                ))}
              </select>
            )}
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, color: color.inkSubtle }}>$</span>
              <Input
                aria-label="Dollars per month"
                inputMode="decimal"
                placeholder="500"
                value={dollars}
                onChange={(event) => setDollars(event.target.value)}
                style={{ width: 100, height: 34, fontSize: 13, padding: '0 10px', borderRadius: radius.md }}
              />
            </span>
            <HeaderButton onClick={() => void save()} disabled={!ready || busy}>
              {busy ? 'Saving' : 'Set budget'}
            </HeaderButton>
          </>
        }
      >
        {(budgets ?? []).map((budget) => (
          <Row key={budget.id} columns={columns}>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block' }}>{budget.label}</span>
              <span style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint }}>{SCOPE_WORDS[budget.scope]}</span>
            </span>
            <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{formatUsd(budget.monthlyMicros)}</span>
            <span style={{ display: 'grid', gap: 4, minWidth: 0 }}>
              <BudgetMeter percent={budget.percent} state={budget.state} />
              <span style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkSubtle }}>{formatUsd(budget.spentMicros)} spent</span>
            </span>
            <RowAction danger onClick={() => void remove(budget.id, budget.label)}>
              Remove
            </RowAction>
          </Row>
        ))}
      </Table>
      {budgets && budgets.length === 0 && (
        <p style={{ margin: '-10px 0 0', fontSize: 13, color: color.inkSubtle }}>No budget yet. One for the organization, a team or a workspace, in dollars a month.</p>
      )}
      <p style={{ margin: '-10px 0 0', fontSize: 12.5, color: color.inkSubtle, lineHeight: 1.55 }}>
        Owners and administrators are mailed at 80 % and at 100 % of a budget, once each a month, and each member’s Airlock shows the budget is near or
        spent. Nothing is stopped: runs go on.
      </p>
      {error && <p style={{ fontSize: 13.5, color: color.badText, margin: 0 }}>{error}</p>}
    </>
  );
}
